'use client'

import { memo, type ComponentType, type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { NinxlGildedOrnament } from './ninxl-renaissance-atmosphere'

export type NinxlGraphicVariant =
  | 'hero-landscape'
  | 'discover'
  | 'compare'
  | 'foundation'
  | 'powers'
  | 'connect'
  | 'commit'
  | 'everywhere'
  | 'pricing'
  | 'faq'
  | 'trust'
  | 'carousel'
  | 'divider'

type GraphicProps = {
  className?: string
}

/** Circuit mesh overlay for hero depth */
export const NinxlCircuitMesh = memo(function NinxlCircuitMesh({ className }: GraphicProps) {
  return (
    <svg
      viewBox="0 0 800 500"
      className={cn('ninxl-circuit-mesh-svg h-full w-full', className)}
      aria-hidden
    >
      <defs>
        <linearGradient id="ninxl-circuit-grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#21728d" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#88a1ac" stopOpacity="0.15" />
        </linearGradient>
      </defs>
      <path d="M80 120 H220 V200 H360" fill="none" stroke="url(#ninxl-circuit-grad)" strokeWidth="1.4" className="ninxl-circuit-line" />
      <path d="M720 90 H580 V180 H440" fill="none" stroke="url(#ninxl-circuit-grad)" strokeWidth="1.4" className="ninxl-circuit-line" />
      <path d="M120 380 H280 V300 H420" fill="none" stroke="url(#ninxl-circuit-grad)" strokeWidth="1.4" className="ninxl-circuit-line" />
      <path d="M680 400 H520 V320 H380" fill="none" stroke="url(#ninxl-circuit-grad)" strokeWidth="1.4" className="ninxl-circuit-line" />
      <circle cx="400" cy="120" r="6" fill="#21728d" fillOpacity="0.35" className="ninxl-graphic-node-pulse" />
    </svg>
  )
})

/** Wide panoramic desk scene: anchored in hero background, not floating */
const HeroLandscapeGraphic = ({ className }: GraphicProps) => (
  <svg viewBox="0 0 1200 360" className={className} aria-hidden preserveAspectRatio="xMidYMax slice">
    <defs>
      <linearGradient id="ninxl-hero-sky" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#21728d" stopOpacity="0" />
        <stop offset="100%" stopColor="#21728d" stopOpacity="0.08" />
      </linearGradient>
      <linearGradient id="ninxl-hero-floor" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#88a1ac" stopOpacity="0.05" />
        <stop offset="50%" stopColor="#21728d" stopOpacity="0.12" />
        <stop offset="100%" stopColor="#88a1ac" stopOpacity="0.05" />
      </linearGradient>
    </defs>
    <rect width="1200" height="360" fill="url(#ninxl-hero-sky)" />
    <ellipse cx="600" cy="300" rx="520" ry="48" fill="url(#ninxl-hero-floor)" />
    {/* Perspective floor lines */}
    {[0, 1, 2, 3, 4, 5].map((i) => (
      <line
        key={i}
        x1={200 + i * 160}
        y1="260"
        x2={320 + i * 112}
        y2="340"
        stroke="#21728d"
        strokeWidth="1"
        strokeOpacity="0.12"
      />
    ))}
    {/* Main monitor */}
    <g transform="translate(420, 108)">
      <rect x="0" y="0" width="360" height="220" rx="18" fill="#f6f9fd" stroke="#21728d" strokeWidth="2" strokeOpacity="0.35" />
      <rect x="16" y="16" width="328" height="172" rx="10" fill="white" stroke="#88a1ac" strokeWidth="1.2" strokeOpacity="0.4" />
      <rect x="32" y="32" width="120" height="8" rx="4" fill="#21728d" fillOpacity="0.2" />
      <rect x="32" y="50" width="200" height="6" rx="3" fill="#88a1ac" fillOpacity="0.25" />
      <rect x="32" y="64" width="160" height="6" rx="3" fill="#88a1ac" fillOpacity="0.2" />
      <rect x="32" y="90" width="280" height="72" rx="12" fill="#f6f9fd" stroke="#21728d" strokeWidth="1" strokeOpacity="0.2" />
      <rect x="48" y="106" width="180" height="6" rx="3" fill="#21728d" fillOpacity="0.3" />
      <rect x="48" y="122" width="120" height="6" rx="3" fill="#88a1ac" fillOpacity="0.25" />
      <rect x="148" y="228" width="64" height="12" rx="4" fill="#21728d" fillOpacity="0.15" />
    </g>
    {/* Side laptop */}
    <g transform="translate(180, 148)">
      <path d="M0 40 L80 8 L160 40 L160 120 L80 152 L0 120 Z" fill="#f6f9fd" stroke="#88a1ac" strokeWidth="1.4" strokeOpacity="0.4" />
      <path d="M80 8 L160 40 L80 72 L0 40 Z" fill="#21728d" fillOpacity="0.1" />
      <rect x="24" y="56" width="112" height="48" rx="6" fill="white" stroke="#21728d" strokeWidth="1" strokeOpacity="0.25" />
    </g>
    {/* Local shield emblem */}
    <g transform="translate(920, 130)">
      <path d="M60 8 L108 20 V72 C108 96 60 112 60 112 C60 112 12 96 12 72 V20 Z" fill="#21728d" fillOpacity="0.12" stroke="#21728d" strokeWidth="1.6" strokeOpacity="0.4" />
      <path d="M44 56 L56 68 L76 44" fill="none" stroke="#21728d" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </g>
    <circle cx="600" cy="80" r="40" fill="#21728d" fillOpacity="0.06" className="ninxl-graphic-node-pulse" />
  </svg>
)

/** Panorama backdrops: wide editorial scenes for section backgrounds */
const DiscoverBackdrop = ({ className }: GraphicProps) => (
  <svg viewBox="0 0 800 240" className={className} aria-hidden preserveAspectRatio="xMidYMid slice">
    <rect width="800" height="240" fill="#21728d" fillOpacity="0.03" />
    {[0, 1, 2, 3].map((i) => (
      <g key={i} transform={`translate(${80 + i * 168}, 40)`}>
        <rect x="0" y={120 - i * 14} width="100" height={80 + i * 14} rx="14" fill="#21728d" fillOpacity={0.06 + i * 0.03} stroke="#21728d" strokeWidth="1.2" strokeOpacity="0.2" />
        <rect x="16" y={100 - i * 14} width="68" height="12" rx="6" fill="#21728d" fillOpacity="0.15" />
      </g>
    ))}
    <path d="M40 200 Q400 140 760 200" fill="none" stroke="#88a1ac" strokeWidth="2" strokeDasharray="6 6" strokeOpacity="0.35" />
  </svg>
)

const CompareBackdrop = ({ className }: GraphicProps) => (
  <svg viewBox="0 0 800 240" className={className} aria-hidden preserveAspectRatio="xMidYMid slice">
    <rect x="80" y="100" width="140" height="90" rx="14" fill="#f6f9fd" stroke="#88a1ac" strokeWidth="1.4" strokeOpacity="0.35" />
    <rect x="330" y="60" width="140" height="130" rx="14" fill="#21728d" fillOpacity="0.1" stroke="#21728d" strokeWidth="2" strokeOpacity="0.35" />
    <rect x="580" y="88" width="140" height="102" rx="14" fill="#f6f9fd" stroke="#88a1ac" strokeWidth="1.4" strokeOpacity="0.35" />
    <text x="400" y="44" textAnchor="middle" className="fill-[#21728d] text-[14px] font-bold">NINX</text>
    <path d="M384 38 L400 20 L416 38" fill="none" stroke="#21728d" strokeWidth="2" strokeLinecap="round" />
  </svg>
)

const FoundationBackdrop = ({ className }: GraphicProps) => (
  <svg viewBox="0 0 800 240" className={className} aria-hidden preserveAspectRatio="xMidYMid slice">
    <rect x="120" y="48" width="560" height="140" rx="24" fill="#f6f9fd" stroke="#21728d" strokeWidth="1.6" strokeOpacity="0.25" />
    <rect x="152" y="76" width="496" height="84" rx="14" fill="white" stroke="#88a1ac" strokeWidth="1.2" strokeOpacity="0.3" />
    <circle cx="220" cy="118" r="22" fill="#21728d" fillOpacity="0.12" />
    <path d="M220 106 V130 M208 118 H232" stroke="#21728d" strokeWidth="2" strokeLinecap="round" />
    <rect x="268" y="104" width="280" height="10" rx="5" fill="#21728d" fillOpacity="0.2" />
    <rect x="268" y="124" width="200" height="10" rx="5" fill="#88a1ac" fillOpacity="0.25" />
    <path d="M580 118 H700" stroke="#88a1ac" strokeWidth="2" strokeDasharray="6 5" strokeOpacity="0.4" />
    <rect x="708" y="96" width="48" height="44" rx="10" fill="#f6f9fd" stroke="#88a1ac" strokeWidth="1.2" strokeOpacity="0.35" />
  </svg>
)

const PowersBackdrop = ({ className }: GraphicProps) => (
  <svg viewBox="0 0 800 240" className={className} aria-hidden preserveAspectRatio="xMidYMid slice">
    <circle cx="400" cy="120" r="56" fill="#21728d" fillOpacity="0.08" stroke="#21728d" strokeWidth="1.6" strokeOpacity="0.3" />
    <ellipse cx="400" cy="120" rx="280" ry="72" fill="none" stroke="#88a1ac" strokeWidth="1.2" strokeDasharray="8 6" strokeOpacity="0.4" className="ninxl-graphic-orbit" />
    {[
      { cx: 120, cy: 120, label: 'Web' },
      { cx: 400, cy: 36, label: 'Shell' },
      { cx: 680, cy: 120, label: 'Mail' },
      { cx: 400, cy: 204, label: 'Agent' },
    ].map((node) => (
      <g key={node.label}>
        <circle cx={node.cx} cy={node.cy} r="24" fill="white" stroke="#21728d" strokeWidth="1.4" strokeOpacity="0.35" />
        <text x={node.cx} y={node.cy + 4} textAnchor="middle" className="fill-[#134b5f] text-[10px] font-semibold">
          {node.label}
        </text>
      </g>
    ))}
  </svg>
)

const ConnectBackdrop = ({ className }: GraphicProps) => (
  <svg viewBox="0 0 800 240" className={className} aria-hidden preserveAspectRatio="xMidYMid slice">
    <circle cx="400" cy="120" r="36" fill="#21728d" fillOpacity="0.14" stroke="#21728d" strokeWidth="1.8" />
    <text x="400" y="125" textAnchor="middle" className="fill-[#134b5f] text-[11px] font-bold">NINX</text>
    {[
      { x: 100, y: 60, label: 'Gmail' },
      { x: 700, y: 60, label: 'Shell' },
      { x: 100, y: 180, label: 'Zerodha' },
      { x: 700, y: 180, label: 'MCP' },
    ].map((node) => (
      <g key={node.label}>
        <line x1="400" y1="120" x2={node.x} y2={node.y} stroke="#88a1ac" strokeWidth="1.2" strokeOpacity="0.45" />
        <rect x={node.x - 40} y={node.y - 18} width="80" height="36" rx="18" fill="white" stroke="#21728d" strokeWidth="1.2" strokeOpacity="0.3" />
        <text x={node.x} y={node.y + 5} textAnchor="middle" className="fill-[#5f7380] text-[10px] font-medium">
          {node.label}
        </text>
      </g>
    ))}
  </svg>
)

const EverywhereBackdrop = ({ className }: GraphicProps) => (
  <svg viewBox="0 0 800 240" className={className} aria-hidden preserveAspectRatio="xMidYMid slice">
    {[
      { x: 160, label: 'Desktop' },
      { x: 400, label: 'Web' },
      { x: 640, label: 'Mobile' },
    ].map((node, i) => (
      <g key={node.label}>
        <rect x={node.x - 56} y="72" width="112" height="96" rx="18" fill="white" stroke="#21728d" strokeWidth="1.4" strokeOpacity={i === 0 ? 0.4 : 0.25} />
        <rect x={node.x - 36} y="92" width="72" height="48" rx="10" fill="#21728d" fillOpacity={0.06 + i * 0.04} />
        <text x={node.x} y="190" textAnchor="middle" className="fill-[#5f7380] text-[10px] font-semibold">
          {node.label}
        </text>
      </g>
    ))}
    <path d="M216 120 H344" stroke="#88a1ac" strokeWidth="1.5" strokeDasharray="5 4" strokeOpacity="0.45" />
    <path d="M456 120 H584" stroke="#88a1ac" strokeWidth="1.5" strokeDasharray="5 4" strokeOpacity="0.45" />
    <circle cx="400" cy="120" r="8" fill="#21728d" fillOpacity="0.35" className="ninxl-graphic-sync-dot" />
  </svg>
)

const PricingBackdrop = ({ className }: GraphicProps) => (
  <svg viewBox="0 0 800 240" className={className} aria-hidden preserveAspectRatio="xMidYMid slice">
    {[
      { x: 200, h: 72, label: 'Free' },
      { x: 400, h: 120, label: 'Pro' },
      { x: 600, h: 156, label: 'Pro+' },
    ].map((tier) => (
      <g key={tier.label}>
        <rect x={tier.x - 60} y={180 - tier.h} width="120" height={tier.h} rx="16" fill="#21728d" fillOpacity="0.08" stroke="#21728d" strokeWidth="1.4" strokeOpacity="0.3" />
        <text x={tier.x} y="200" textAnchor="middle" className="fill-[#134b5f] text-[10px] font-semibold">
          {tier.label}
        </text>
      </g>
    ))}
    <path d="M80 180 H720" stroke="#88a1ac" strokeWidth="1.5" strokeOpacity="0.35" />
  </svg>
)

const FaqBackdrop = ({ className }: GraphicProps) => (
  <svg viewBox="0 0 800 240" className={className} aria-hidden preserveAspectRatio="xMidYMid slice">
    <rect x="120" y="48" width="400" height="56" rx="28" fill="white" stroke="#21728d" strokeWidth="1.4" strokeOpacity="0.3" />
    <rect x="280" y="136" width="400" height="56" rx="28" fill="#21728d" fillOpacity="0.08" stroke="#21728d" strokeWidth="1.4" strokeOpacity="0.35" />
    <circle cx="120" cy="76" r="12" fill="#21728d" fillOpacity="0.12" />
    <circle cx="680" cy="164" r="12" fill="#21728d" fillOpacity="0.15" />
  </svg>
)

const TrustBackdrop = ({ className }: GraphicProps) => (
  <svg viewBox="0 0 800 120" className={className} aria-hidden preserveAspectRatio="xMidYMid slice">
    <path d="M120 20 L180 8 V72 C180 96 120 108 120 108 C120 108 60 96 60 72 V8 Z" fill="#21728d" fillOpacity="0.1" stroke="#21728d" strokeWidth="1.6" strokeOpacity="0.35" transform="translate(40, 0)" />
    <path d="M104 56 L116 68 L136 44" fill="none" stroke="#21728d" strokeWidth="2.5" strokeLinecap="round" transform="translate(40, 0)" />
    {['Offline', '~20MB', '<3s', 'CASA'].map((label, i) => (
      <g key={label} transform={`translate(${280 + i * 120}, 44)`}>
        <rect x="-44" y="0" width="88" height="32" rx="16" fill="white" stroke="#88a1ac" strokeWidth="1.2" strokeOpacity="0.35" />
        <text x="0" y="20" textAnchor="middle" className="fill-[#134b5f] text-[9px] font-semibold">
          {label}
        </text>
      </g>
    ))}
  </svg>
)

const CommitBackdrop = ({ className }: GraphicProps) => (
  <svg viewBox="0 0 800 240" className={className} aria-hidden preserveAspectRatio="xMidYMid slice">
    <path d="M280 80 L400 40 L520 80 L520 160 L400 200 L280 160 Z" fill="#21728d" fillOpacity="0.1" stroke="#21728d" strokeWidth="1.8" strokeOpacity="0.35" />
    <path d="M340 116 L372 140 L460 96" fill="none" stroke="#21728d" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="400" cy="120" r="48" fill="none" stroke="#21728d" strokeWidth="1.2" strokeOpacity="0.18" className="ninxl-graphic-pulse-ring" />
  </svg>
)

const CarouselBackdrop = ({ className }: GraphicProps) => (
  <svg viewBox="0 0 800 120" className={className} aria-hidden preserveAspectRatio="xMidYMid slice">
    <rect x="80" y="24" width="640" height="72" rx="20" fill="#f6f9fd" stroke="#21728d" strokeWidth="1.4" strokeOpacity="0.25" />
    <rect x="108" y="44" width="200" height="32" rx="10" fill="white" stroke="#88a1ac" strokeWidth="1.2" strokeOpacity="0.35" />
    <rect x="340" y="52" width="120" height="8" rx="4" fill="#21728d" fillOpacity="0.22" />
    <rect x="340" y="68" width="80" height="8" rx="4" fill="#88a1ac" fillOpacity="0.28" />
    <circle cx="660" cy="60" r="18" fill="#21728d" fillOpacity="0.12" stroke="#21728d" strokeWidth="1.4" />
  </svg>
)

/** Hero landscape: fixed ambient asset behind the page */
export const NinxlHeroLandscape = memo(function NinxlHeroLandscape({ className }: GraphicProps) {
  return (
    <div className={cn('ninxl-hero-landscape pointer-events-none absolute inset-x-0 bottom-0', className)} aria-hidden>
      <HeroLandscapeGraphic className="h-full w-full min-h-[280px] opacity-[0.55]" />
    </div>
  )
})

/** Section backdrop: wide scene wash anchored to section, not floating icons */
export const NinxlSectionBackdrop = memo(function NinxlSectionBackdrop({
  variant,
  position = 'bottom',
  className,
}: {
  variant: NinxlGraphicVariant
  position?: 'top' | 'bottom' | 'center'
  className?: string
}) {
  const Backdrop = BACKDROP_MAP[variant]
  if (!Backdrop) return null

  const positionClass = {
    top: 'items-start pt-0',
    bottom: 'items-end pb-0',
    center: 'items-center',
  }[position]

  return (
    <div
      className={cn(
        'ninxl-section-backdrop pointer-events-none absolute inset-0 -z-10 flex overflow-hidden opacity-[0.42]',
        positionClass,
        className
      )}
      aria-hidden
    >
      <Backdrop className="h-[55%] min-h-[180px] w-full max-w-5xl mx-auto" />
    </div>
  )
})

/** Wave divider between sections */
export const NinxlSectionDivider = memo(function NinxlSectionDivider({ className }: GraphicProps) {
  return (
    <div className={cn('ninxl-section-divider mx-auto max-w-4xl px-6 py-8', className)} aria-hidden>
      <NinxlGildedOrnament className="mb-3 w-56" />
      <svg viewBox="0 0 400 28" className="h-7 w-full">
        <path
          d="M0 14 C60 6 100 22 200 14 S340 6 400 14"
          fill="none"
          stroke="var(--ninxl-gilded)"
          strokeWidth="1.2"
          strokeOpacity="0.35"
        />
        <path
          d="M0 14 C60 10 100 18 200 14 S340 10 400 14"
          fill="none"
          stroke="var(--ninxl-ultramarine)"
          strokeWidth="1.5"
          strokeOpacity="0.28"
        />
        <circle cx="200" cy="14" r="4" fill="var(--ninxl-gilded-light)" fillOpacity="0.55" className="ninxl-graphic-node-pulse" />
      </svg>
    </div>
  )
})

/** Carousel shell: graphic baked into frame border, not floating */
export const NinxlCarouselFrame = memo(function NinxlCarouselFrame({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div className={cn('ninxl-carousel-shell relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-b from-[#f6f9fd] to-white p-6 md:p-8 shadow-sm', className)}>
      <div className="ninxl-carousel-frame-wash pointer-events-none absolute inset-x-0 top-0 h-24 opacity-30" aria-hidden>
        <CarouselBackdrop className="h-full w-full" />
      </div>
      <div className="relative mb-6 text-center sm:text-left">
        <div className="text-xs tracking-widest text-slate-500 mb-1">SEE IT IN ACTION</div>
        <p className="text-sm text-slate-400">Real demos across Gmail, brokers, shell, and more</p>
      </div>
      <div className="relative">{children}</div>
    </div>
  )
})

const BACKDROP_MAP: Partial<Record<NinxlGraphicVariant, ComponentType<GraphicProps>>> = {
  discover: DiscoverBackdrop,
  compare: CompareBackdrop,
  foundation: FoundationBackdrop,
  powers: PowersBackdrop,
  connect: ConnectBackdrop,
  everywhere: EverywhereBackdrop,
  pricing: PricingBackdrop,
  faq: FaqBackdrop,
  trust: TrustBackdrop,
  commit: CommitBackdrop,
  carousel: CarouselBackdrop,
  'hero-landscape': HeroLandscapeGraphic,
}

const GRAPHIC_MAP: Record<NinxlGraphicVariant, ComponentType<GraphicProps>> = {
  'hero-landscape': HeroLandscapeGraphic,
  discover: DiscoverBackdrop,
  compare: CompareBackdrop,
  foundation: FoundationBackdrop,
  powers: PowersBackdrop,
  connect: ConnectBackdrop,
  commit: CommitBackdrop,
  everywhere: EverywhereBackdrop,
  pricing: PricingBackdrop,
  faq: FaqBackdrop,
  trust: TrustBackdrop,
  carousel: CarouselBackdrop,
  divider: () => null,
}

export const NinxlGraphic = memo(function NinxlGraphic({
  variant,
  className,
}: {
  variant: NinxlGraphicVariant
  className?: string
}) {
  const Graphic = GRAPHIC_MAP[variant]
  if (!Graphic || variant === 'divider') return null
  return <Graphic className={className} />
})

/** Section header: typography only; visuals live in NinxlSectionBackdrop */
export const NinxlSectionHeader = memo(function NinxlSectionHeader({
  label,
  title,
  description,
  className,
}: {
  label: string
  title: string
  description?: string
  className?: string
}) {
  return (
    <div className={cn('ninxl-section-header relative text-center mb-10', className)}>
      <NinxlGildedOrnament className="mb-5 w-56 opacity-95" />
      <div className="ninxl-fresco-label text-xs uppercase tracking-[3px] mb-3">{label}</div>
      <h2 className="ninxl-fresco-title text-4xl font-semibold tracking-tight mb-3">{title}</h2>
      {description ? <p className="max-w-2xl mx-auto text-slate-600">{description}</p> : null}
    </div>
  )
})